import { useFeatureFlags } from "@client/hooks/use-feature-flag-enabled";
import { HomeTeams } from "./HomeTeams";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import LoadingSpinner from "@client/components/utils/LoadingSpinner";
import { Page } from "@client/components/utils/Page";
import { RoundWizard } from "@client/components/wizard/RoundWizard";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { useImpersonationRefValue } from "@client/ImperativeState";
import {
  isIPadOffline,
  isIPadOfflineFailure,
  OFFLINE_CACHE_KEYS,
  readOfflineCache,
  writeOfflineCacheIfIPad,
} from "@client/offline/offlineCache";
import { trpc } from "@client/trpc";
import { isDemoRoundName } from "@lib/constants";
import {
  Button,
  Card,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Input,
  useLocalStorage,
} from "@sfc/ui";
import { ChevronDownIcon, ChevronRight, DownloadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { downloadRoundScopedFile } from "@client/lib/downloadRoundScopedFile";
import { toast } from "sonner";
import { Onboarding } from "../../components/wizard/Onboarding";
import { resolveHomepageView } from "./resolveHomepageView";
import { RoundActionButton } from "./RoundActionButton";

const DownloadRoundButton = withErrorBoundary(function DownloadRoundButton({
  roundId,
  roundName,
}: {
  roundId: string;
  roundName: string;
}) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload(e: React.MouseEvent) {
    e.preventDefault();
    setDownloading(true);

    try {
      const result = await downloadRoundScopedFile({
        path: `/api/download-round?roundId=${encodeURIComponent(roundId)}`,
        filename: `${roundName}_database.json`,
      });
      if (!result.ok) {
        toast.error(result.error);
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <RoundActionButton isLoading={downloading} onClick={handleDownload}>
      <DownloadIcon />
      Download
    </RoundActionButton>
  );
});

// Admin-only quick links to jump straight into a round's Proposals page and
// funder Review tab from the homepage.
function RoundAdminShortcuts({ roundId }: { roundId: string }) {
  return (
    <>
      <RoundActionButton href={`/round/${roundId}/proposals`}>
        Proposals
      </RoundActionButton>
      <RoundActionButton href={`/round/${roundId}/funder/review`}>
        Review
      </RoundActionButton>
    </>
  );
}

// One row of a round list. Both lists on this page — the rounds you are in and
// the admin-only "rounds you are not participating in" — render through this so
// the row hover and action styling can only be defined once.
function RoundListRow({
  roundId,
  roundName,
  archivedAt,
  showAdminShortcuts,
  showDownload,
}: {
  roundId: string;
  roundName: string;
  archivedAt?: Date | string | null;
  showAdminShortcuts: boolean;
  showDownload: boolean;
}) {
  return (
    <li className="group border-b last:border-b-0 border-border flex flex-wrap items-center">
      <a
        className="flex-1 basis-full sm:basis-0 min-w-0 px-5 py-5 heading-sm hover:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring flex items-center gap-4"
        href={`/round/${roundId}/`}
      >
        <span className="flex-1 min-w-0 [overflow-wrap:anywhere]">
          {roundName}
          {archivedAt != null && (
            <span className="text-xs font-normal text-muted-foreground">
              {" "}
              Archived{" "}
              {new Date(archivedAt).toLocaleDateString(undefined, {
                dateStyle: "medium",
              })}
            </span>
          )}
        </span>
        <ChevronRight
          className="size-4 shrink-0 text-primary"
          aria-hidden="true"
        />
      </a>
      {(showAdminShortcuts || showDownload) && (
        <div className="flex flex-wrap items-center gap-2 ml-auto px-4 pb-3 sm:pb-0">
          {showAdminShortcuts && <RoundAdminShortcuts roundId={roundId} />}
          {showDownload && (
            <DownloadRoundButton roundId={roundId} roundName={roundName} />
          )}
        </div>
      )}
    </li>
  );
}

function AdminDemoRoundTool() {
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [lastCreatedRound, setLastCreatedRound] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const createDemoRound = trpc.createDemoRound.useMutation({
    onSuccess: async (result) => {
      if (result.status === "needsName") {
        setNeedsName(true);
        toast.error("Enter a name for this demo user and submit again.");
        return;
      }

      setLastCreatedRound({
        id: result.roundId,
        name: result.roundName,
      });
      setEmail("");
      setName("");
      setNeedsName(false);
      toast.success(`Created ${result.roundName}`);
      await utils.getHomepageData.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create demo round");
    },
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const normalizedEmail = email.trim();
    const normalizedName = name.trim();

    if (normalizedEmail.length === 0) {
      toast.error("Email is required");
      return;
    }

    createDemoRound.mutate({
      email: normalizedEmail,
      name: needsName ? normalizedName || undefined : undefined,
    });
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="p-0 overflow-hidden">
        <CollapsibleTrigger
          render={
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent"
            />
          }
        >
          <div>
            <h2 className="text-sm font-medium">Admin demo round tool</h2>
            <p className="text-xs text-muted-foreground">
              Manually create a demo round for a user.
            </p>
          </div>
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <form
            className="border-t px-4 py-4 flex flex-col gap-3"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="demo-round-email" className="text-sm font-medium">
                User Email
              </label>
              <p className="text-xs text-muted-foreground">
                Enter the email for the user you are creating this demo round
                for.
              </p>
              <Input
                id="demo-round-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setNeedsName(false);
                }}
                placeholder="user@example.com"
                required
              />
            </div>

            {needsName && (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="demo-round-name"
                  className="text-sm font-medium"
                >
                  Name
                </label>
                <Input
                  id="demo-round-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="User Name"
                  required
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" isLoading={createDemoRound.isPending}>
                Create Test Round
              </Button>
              {lastCreatedRound && (
                <a
                  href={`/round/${lastCreatedRound.id}/`}
                  className="text-sm underline"
                >
                  Open {lastCreatedRound.name}
                </a>
              )}
            </div>
          </form>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Archived rounds from both lists, collapsed at the bottom of the page. They
// stay reachable (an archived round is read-only, not gone) without crowding
// the rounds people are actually working in. Restoring happens inside the
// round, in Round Settings, so there is one restore path.
function ArchivedRounds({
  rounds,
  isSfcAdmin,
}: {
  rounds: { id: string; name: string; archivedAt: Date | string | null }[];
  isSfcAdmin: boolean;
}) {
  const [open, setOpen] = useLocalStorage<boolean>(
    "homepageShowArchivedRounds",
    false,
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="py-0 overflow-hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
          <div>
            <h2 className="text-sm font-medium">
              Archived rounds ({rounds.length})
            </h2>
            <p className="text-xs text-muted-foreground">
              Read-only until restored from their Round Settings.
            </p>
          </div>
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="border-t">
            {rounds.map((round) => (
              <RoundListRow
                key={round.id}
                roundId={round.id}
                roundName={round.name}
                archivedAt={round.archivedAt}
                showAdminShortcuts={isSfcAdmin}
                showDownload={false}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Admin-only list of all rounds the viewer is not a member of, with a toggle
// to hide demo rounds (identified by name — they have no DB flag) since they
// quickly outnumber the real rounds.
function NonParticipatingRounds({
  rounds,
  offline,
}: {
  rounds: { id: string; name: string }[];
  offline: boolean;
}) {
  const [showDemoRounds, setShowDemoRounds] = useLocalStorage<boolean>(
    "homepageShowDemoRounds",
    false,
  );

  const demoRoundCount = rounds.filter((round) =>
    isDemoRoundName(round.name),
  ).length;
  const visibleRounds = showDemoRounds
    ? rounds
    : rounds.filter((round) => !isDemoRoundName(round.name));

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="heading-sm">Other Rounds</h3>
        {demoRoundCount > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showDemoRounds}
              onCheckedChange={(checked) => setShowDemoRounds(checked === true)}
            />
            Show demo rounds ({demoRoundCount})
          </label>
        )}
      </div>
      {visibleRounds.length > 0 && (
        <Card className="py-0 overflow-hidden">
          <ul>
            {visibleRounds.map((round) => (
              <RoundListRow
                key={round.id}
                roundId={round.id}
                roundName={round.name}
                showAdminShortcuts={true}
                showDownload={!offline}
              />
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

export function Home() {
  const featureFlags = useFeatureFlags();
  const teamsEnabled = featureFlags.data?.teams;
  const query = trpc.getHomepageData.useQuery();
  type HomepageData = NonNullable<typeof query.data>;

  // getHomepageData is anonymized per the *effective* user (ctx.getUser()), so
  // its offline cache must be scoped to that user — otherwise an admin who
  // cached the page while impersonating could be served that user's round list
  // offline. The effective user is the impersonated user when impersonating,
  // else the underlying logged-in user (offline-stable via useCurrentUser).
  const { data: currentUser } = useCurrentUser();
  const impersonation = useImpersonationRefValue();
  const effectiveUserId = impersonation ?? currentUser?.userId ?? null;

  // Mirror the homepage into the iPad offline cache on success, and serve it
  // back when offline so a funder can still navigate to a cached round. Only
  // write once the query has refetched for the CURRENT effective user:
  // getHomepageData reuses the same React Query key across an impersonation
  // switch (the viewer rides in a header), so right after a switch `query.data`
  // still holds the previous viewer's round list. `query.data.user.id` is the
  // viewer the payload is for — gating on it prevents writing stale data under
  // the new user's key (which they could then see offline).
  useEffect(() => {
    if (
      query.data?.user?.id != null &&
      query.data.user.id === effectiveUserId
    ) {
      writeOfflineCacheIfIPad(
        OFFLINE_CACHE_KEYS.homepageData(effectiveUserId),
        query.data,
      );
    }
  }, [query.data, effectiveUserId]);

  // `error ?? failureReason`, because a refetch resets a dataless errored
  // query to pending with `error` cleared (SP-2612) — `failureReason` still
  // carries the in-flight attempt's transport error, so the cached round list
  // keeps rendering instead of flapping back to the loading skeleton.
  const queryFailure = query.error ?? query.failureReason;
  const offline =
    isIPadOffline() ||
    (queryFailure != null && isIPadOfflineFailure(queryFailure));
  const cachedData =
    offline && effectiveUserId != null
      ? readOfflineCache<HomepageData>(
          OFFLINE_CACHE_KEYS.homepageData(effectiveUserId),
        )
      : null;
  // Scope the rendered data to the current effective user (never expose another
  // viewer's payload from the shared query key), prefer the offline cache when
  // the live data isn't usable, and resolve loading/error without spinning
  // forever on a failed post-impersonation refetch. Pure + unit-tested.
  const { data, isLoading, isError } = resolveHomepageView<HomepageData>({
    queryData: query.data,
    cachedData,
    effectiveUserId,
    isQueryLoading: query.isLoading,
    isQueryError: query.isError,
  });

  const visibleRounds = [
    ...(data?.user?.participating_in ?? []),
    ...(data?.nonParticipatingRounds ?? []).map((round) => ({
      round,
      track: "NO_TRACK",
    })),
  ];
  // Archived rounds leave both lists and gather in one collapsed section.
  const liveParticipatingIn = (data?.user?.participating_in ?? []).filter(
    ({ round }) => round.archivedAt == null,
  );
  const liveNonParticipating = (data?.nonParticipatingRounds ?? []).filter(
    (round) => round.archivedAt == null,
  );
  const archivedRounds = visibleRounds
    .map(({ round }) => round)
    .filter((round) => round.archivedAt != null);

  if (
    teamsEnabled === false &&
    !offline &&
    !isError &&
    !isLoading &&
    visibleRounds.length === 0
  ) {
    return (
      <Onboarding
        isSfcAdmin={data?.user?.isSfcAdmin ?? false}
        before={
          data?.user?.isSfcAdmin && (
            <div className="mx-auto w-full max-w-4xl px-4 pt-6 md:px-8">
              <AdminDemoRoundTool />
            </div>
          )
        }
      />
    );
  }

  const createRoundDialog = data?.user?.isSfcAdmin && !offline && (
    <Dialog>
      <DialogTrigger
        render={
          <Button
            variant="nav"
            className="self-start pointer-coarse:min-h-11"
          />
        }
      >
        Create Round
      </DialogTrigger>
      <DialogContent className="bg-background">
        <DialogTitle className="text-center">Create Round</DialogTitle>
        <RoundWizard isSfcAdmin={data.user.isSfcAdmin} />
      </DialogContent>
    </Dialog>
  );

  return (
    <Page spacing="compact" className="flex flex-col gap-8">
      <h1 className="sr-only">Home</h1>
      {isLoading ? (
        <LoadingSpinner />
      ) : isError || !data?.user ? (
        <p className="text-muted-foreground">
          An error occurred while loading the homepage. Please try refreshing
          the page.
        </p>
      ) : (
        <>
          {offline && visibleRounds.length === 0 && (
            <p className="body-md text-text-secondary">
              You’re offline. Connect to the internet to view your teams and
              available rounds.
            </p>
          )}
          {!offline &&
            teamsEnabled === undefined &&
            visibleRounds.length === 0 &&
            (featureFlags.isError ? (
              <div role="alert" className="flex flex-col items-start gap-2">
                <p>Couldn’t load your workspace. Please try again.</p>
                <Button
                  variant="nav"
                  onClick={() => void featureFlags.refetch()}
                >
                  Try again
                </Button>
              </div>
            ) : (
              <LoadingSpinner />
            ))}
          {!offline && (
            <HomeTeams
              key={data.user.id}
              viewerId={data.user.id}
              showEmpty={data.user.participating_in.length === 0}
            />
          )}
          {liveParticipatingIn.length > 0 && (
            <section
              aria-labelledby="home-recommending-heading"
              className="flex flex-col gap-4"
            >
              <div>
                <h2 id="home-recommending-heading" className="heading-lg">
                  Recommending
                </h2>
                <p className="body-md text-text-secondary">
                  The rounds you’re participating in.
                </p>
              </div>
              <Card className="py-0 overflow-hidden">
                <ul>
                  {liveParticipatingIn.map(({ round, track }) => (
                    <RoundListRow
                      key={round.id}
                      roundId={round.id}
                      roundName={round.name}
                      showAdminShortcuts={data.user?.isSfcAdmin ?? false}
                      showDownload={
                        !offline &&
                        (track === "NO_TRACK" || !!data.user?.isSfcAdmin)
                      }
                    />
                  ))}
                </ul>
              </Card>
            </section>
          )}

          {data.user.isSfcAdmin && (
            <section
              aria-label="Admin tools"
              className="flex flex-col gap-6 border-t border-border pt-6"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="heading-lg">Admin Tools</h2>
                {createRoundDialog}
              </div>
              {liveNonParticipating.length > 0 && (
                <NonParticipatingRounds
                  rounds={liveNonParticipating}
                  offline={offline}
                />
              )}
              <AdminDemoRoundTool />
            </section>
          )}
          {archivedRounds.length > 0 && (
            <ArchivedRounds
              rounds={archivedRounds}
              isSfcAdmin={data.user.isSfcAdmin}
            />
          )}
        </>
      )}
    </Page>
  );
}
